// Import media
import userLoopBannerImg from './img/user-loop-banner.svg';

// Import dependencies
import { html, LitElement, render } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

// Import global utils
import { versionCompare } from '@automator/shared/utils/version-compare';
import { addParametersToURL } from '@automator/shared/utils/add-parameters-to-url';
import { addParametersUTM } from '@automator/shared/utils/add-parameters-utm-to-url';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x } from '@wordpress/i18n';

// Import recipe builder services
import { createLoop } from '@automator/builder/services/item/loop';

/**
 * Custom element representing the Automator Builder Loop Add User loop button.
 *
 * This element renders a button to add a user loop in the Automator Builder.
 * The button will only render based on specific conditions related to the user's subscription (Pro or not) and the version of the plugin they are using.
 */
@customElement( 'uap-builder-loop-add-user-loop' ) // eslint-disable-line require-jsdoc
export class AutomatorBuilderLoopAddUserLoop extends LitElement {
	/**
	 * The size of the button.
	 *
	 * @type {String}
	 */
	@property( { type: String } ) accessor size;

	/**
	 * The color of the button.
	 * Defaults to "secondary".
	 *
	 * @type {String}
	 */
	@property( { type: String } ) accessor color = 'secondary';

	/**
	 * Renders the button element if the conditions are met.
	 *
	 * @return {TemplateResult|void} The HTML template for the button or nothing if it shouldn't render.
	 */
	render() {
		// If the button shouldn't render, return nothing
		if ( ! this.shouldRender() ) {
			return;
		}

		return html`
			<uo-button
				size=${ this.size }
				color=${ this.color }
				@click=${ this.handleClick }
			>
				${ this._templateLabel }
			</uo-button>
		`;
	}

	/**
	 * Returns the template for the label.
	 *
	 * Depending on whether the user has a Pro subscription, the label will be different.
	 *
	 * @return {TemplateResult} The template for the label.
	 */
	get _templateLabel() {
		if ( UncannyAutomatorBackend._site.automator.is_pro_active ) {
			return html`${ escapeHTML( __( 'User loop', 'uncanny-automator' ) ) }`;
		}

		return html`
			${ escapeHTML( __( 'User loop', 'uncanny-automator' ) ) } <uo-pro-tag size=${ ifDefined( this.size ) }></uo-pro-tag>
		`;
	}

	/**
	 * Handles the click event on the button.
	 *
	 * This method is responsible for determining what to do when the user clicks the button.
	 * — If the user is not a Pro user, it will show the upsell dialog.
	 * — If the user is a Pro user, it will add a user loop to the recipe.
	 *
	 * @return {undefined}
	 */
	handleClick() {
		// If the user doesn't have Pro, show the upsell dialog
		if ( ! UncannyAutomatorBackend._site.automator.is_pro_active ) {
			this.handleCreateUpsellDialog();
			return;
		}

		// Otherwise, add the user loop
		this.handleAddUserLoop();
	}

	/**
	 * Handles the action of adding a loop to the recipe.
	 *
	 * This method interacts with the UncannyAutomator core to add a loop of type 'users'.
	 */
	handleAddUserLoop() {
		// Add a user loop
		createLoop( {
			type: 'users'
		} );
	}

	/**
	 * Creates the dialog that explains to non-Pro users what this feature does and how they can get it.
	 */
	handleCreateUpsellDialog() {
		// Create the node that we'll use to render the dialog
		// This node will be added as a direct child of the body node.
		// We can't add it inside the component created on this class because of z-index issues
		const $wrapper = document.createElement( 'div' );
		document.body.append( $wrapper );

		render(
			html`
				<uo-dialog
					heading=${ escapeHTML( __( 'User loop', 'uncanny-automator' ) ) }
					featured-image=${ userLoopBannerImg }
					featured-image-position="center bottom"
					size="large"
					open
				>

					<p>
						<uo-markdown 
							allowed-tags="strong"
							.content=${ escapeHTML( __( 'A powerful admin tool, user loops let you perform **bulk actions** on any set of **users that meet specific criteria**.', 'uncanny-automator' ) ) }>
						</uo-markdown>
					</p>

					<p>
						${ escapeHTML( __( 'Here are a few examples of what is possible with user loops:', 'uncanny-automator' ) ) }
					</p>

					<ul>
						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'Send an email or coupon code to all users that **purchased a specific product** or are a **member of a specific group**.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>

						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'Add a role to **users with a specific role** or **with a specific membership/access level**.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>

						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'Export a list of **users matching specific profile criteria** to Google Sheets, AirTable or Notion.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>

						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'Perform actions in bulk on **users with specific usermeta**.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>
					</ul>
					
					<uo-button
						slot="primary-action"

						href=${ this.upsaleURL }
						target="_blank"

						dialog-action="close"
					>
						${ escapeHTML( __( 'Get User loops', 'uncanny-automator' ) ) }
					</uo-button>

					<uo-button
						slot="secondary-action"
						dialog-action="close"
						color="secondary"
					>
						${ escapeHTML( _x( 'Cancel', 'Verb', 'uncanny-automator' ) ) }
					</uo-button>
				</uo-dialog>
			`,
			$wrapper
		);
	}

	/**
	 * The URL to the upsell page. This URL will include UTM parameters to track the user's journey.
	 *
	 * @return {String} The URL to the upsell page.
	 */
	get upsaleURL() {
		return addParametersToURL(
			// Add UTM parameters
			addParametersUTM( 'https://automatorplugin.com/pricing/', {
				medium: 'recipe_creator',
				content: 'add_user_loop_button',
			} ),
			// Add context to show banner in the pricing page
			{
				uap_bpro_context: 'user_loop'
			}
		)
	}

	/**
	 * Determines whether or not the button should render.
	 *
	 * The button will render based on the user's subscription status and the plugin version.
	 *
	 * @return {Boolean} Whether or not the button should render.
	 */
	shouldRender() {
		// If the user doesn't have Pro, render it because we're going to upsell the feature
		if ( ! UncannyAutomatorBackend._site.automator.is_pro_active ) {
			return true;
		}

		// If the user has Pro, only render it if they have version 5.3 or higher
		return versionCompare(
			UncannyAutomator._site.automator.version_pro,
			'5.3',
			'>='
		);
	}
}
